import { auth } from '../auth';
import { getBreakGlassToken, clearBreakGlassToken } from '../auth/break-glass-auth';
import { API_BASE_URL } from '../utils';

/**
 * Request options extending fetch RequestInit.
 */
interface RequestOptions extends RequestInit {
  /** URL query parameters */
  params?: Record<string, string>;
  /** Skip retry for this request */
  noRetry?: boolean;
}

/**
 * Retry configuration for the API client.
 */
interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Base delay in milliseconds */
  baseDelayMs: number;
  /** Maximum delay in milliseconds */
  maxDelayMs: number;
  /** HTTP status codes that should trigger a retry */
  retryableStatuses: number[];
}

/**
 * Default retry configuration.
 * @see Spec 0012 Section 8.1 - API Client with Retry Strategy (OPS-02)
 */
const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelayMs: 1000, // 1 second
  maxDelayMs: 10000, // 10 seconds max
  retryableStatuses: [503, 502, 504], // Service Unavailable, Bad Gateway, Gateway Timeout
};

/**
 * Calculate delay with exponential backoff and jitter.
 */
function calculateDelay(attempt: number, config: RetryConfig): number {
  const exponentialDelay = config.baseDelayMs * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // 0-30% jitter
  return Math.min(exponentialDelay + jitter, config.maxDelayMs);
}

/**
 * Sleep for specified milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if HTTP status is retryable.
 */
function isRetryable(status: number, config: RetryConfig): boolean {
  return config.retryableStatuses.includes(status);
}

/**
 * Authentication error - session expired or invalid.
 */
export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Rate limit error - too many requests.
 */
export class RateLimitError extends Error {
  /** Seconds to wait before retrying */
  retryAfter: number;

  constructor(retryAfter: number) {
    super(`Rate limited. Retry after ${retryAfter} seconds.`);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

/**
 * Network error - connection failed.
 */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * API error - server returned an error response.
 */
export class ApiError extends Error {
  /** HTTP status code */
  status: number;
  /** Error code from API */
  code: string;
  /** Field-level validation errors from INVALID_PARAMETERS responses */
  details?: Array<{ field: string; message: string }>;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Array<{ field: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * API client with automatic auth token injection and retry strategy.
 *
 * Features:
 * - Automatic Bearer token injection
 * - Exponential backoff with jitter for transient errors
 * - Proper error classes for different error types
 * - Same-origin API calls via CloudFront proxy
 *
 * @see Spec 0012 Section 8.1 - Base Client with Retry Strategy
 */
export async function apiClient<T>(
  endpoint: string,
  options: RequestOptions = {},
  retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG
): Promise<T> {
  const { params, noRetry, ...fetchOptions } = options;

  // Break-Glass Token hat Vorrang (kurzlebig, Tab-gebunden — Spec 0044)
  const breakGlassToken = getBreakGlassToken();
  const token = breakGlassToken || await auth.getIdToken();
  if (!token) {
    throw new AuthError('Not authenticated');
  }

  // Build URL with query params (relative URL for same-origin via CloudFront)
  const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  let lastError: Error | null = null;
  const maxAttempts = noRetry ? 1 : retryConfig.maxRetries + 1;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await fetch(url.toString(), {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...fetchOptions.headers,
        },
      });

      // Handle rate limiting (don't retry, let caller handle)
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        throw new RateLimitError(retryAfter ? parseInt(retryAfter, 10) : 60);
      }

      // Handle auth errors (don't retry)
      if (response.status === 401) {
        clearBreakGlassToken(); // Spec 0044: Break-Glass Token loeschen
        await auth.signOut();
        throw new AuthError('Session expired');
      }

      // Handle retryable server errors
      if (!response.ok) {
        if (isRetryable(response.status, retryConfig) && attempt < maxAttempts - 1) {
          const delay = calculateDelay(attempt, retryConfig);
          if (import.meta.env.DEV) console.warn(`API request failed (${response.status}), retrying in ${delay}ms...`);
          await sleep(delay);
          continue;
        }

        const error = await response.json().catch(() => ({}));
        throw new ApiError(
          response.status,
          error.code || 'UNKNOWN_ERROR',
          error.message || `Request failed with status ${response.status}`,
          error.details,
        );
      }

      // Empty-body success responses must be handled BEFORE the JSON-parse branch.
      // API Gateway attaches a default `content-type: application/json` header even to an
      // empty 204 body — calling response.json() on it would throw "Unexpected end of JSON
      // input" and surface as a false error toast on every 204 endpoint (asset/service
      // delete, subscription archive, ...). See issue #378.
      // (304 is intentionally not handled here: fetch reports it as !response.ok, so the
      // error branch above already short-circuits before reaching this point.)
      if (
        response.status === 204 ||
        response.status === 205 ||
        response.headers.get('content-length') === '0'
      ) {
        return {} as T;
      }

      // Parse JSON response
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return response.json();
      }

      // Unexpected non-JSON response (e.g. CloudFront error page returning HTML)
      throw new ApiError(
        response.status,
        'UNEXPECTED_RESPONSE',
        `Expected JSON but received ${contentType || 'unknown content type'}`
      );
    } catch (err) {
      // Re-throw known error types
      if (
        err instanceof AuthError ||
        err instanceof RateLimitError ||
        err instanceof ApiError
      ) {
        throw err;
      }

      // Network errors (fetch failed)
      if (err instanceof TypeError && (err.message.includes('fetch') || err.message.includes('network'))) {
        lastError = new NetworkError('Network unavailable');
        if (attempt < maxAttempts - 1) {
          const delay = calculateDelay(attempt, retryConfig);
          if (import.meta.env.DEV) console.warn(`Network error, retrying in ${delay}ms...`);
          await sleep(delay);
          continue;
        }
      }

      throw err;
    }
  }

  throw lastError ?? new Error('Request failed after retries');
}

/**
 * GET request helper.
 */
export async function get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  return apiClient<T>(endpoint, { method: 'GET', params });
}

/**
 * POST request helper.
 */
export async function post<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiClient<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request helper.
 */
export async function put<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiClient<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PATCH request helper.
 */
export async function patch<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiClient<T>(endpoint, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request helper.
 */
export async function del<T>(endpoint: string): Promise<T> {
  return apiClient<T>(endpoint, { method: 'DELETE' });
}
